/* ---------------------------------------------------------------------------
   Stream state. Zustand owns this; TanStack Query owns everything with a URL.

   The rule is not stylistic (initial-plan.md § Frontend). A run emits dozens of
   frames a second — on this surface more, because the expert stream carries the
   whole tool stream and the thinking trace with no filtering. Pushing each frame
   through `setQueryData` re-runs every structural-sharing comparison in the
   cache and re-renders every observer of that key. So Query keeps the chat
   document, which changes on a fetch, and this store keeps the firehose, which
   changes on a socket.

   The one place the two must meet is `refetchToken`. Some frames tell us that
   something the stream does NOT carry has moved — the composer predicate, the
   promotion gate, the diff — and the honest response is to re-fetch the
   document rather than to guess at the new value. Every such frame bumps the
   token and the review page's effect re-fetches.
--------------------------------------------------------------------------- */
import { create } from 'zustand';
import type {
  AskBlock,
  ChatMessage,
  ChatStatus,
  ClientChatStatus,
  ComposerState,
  Promotion,
  PromotionFailure,
  PromotionRepo,
  PromotionStatus,
  PromotionTarget,
  RunSummary,
  SseExpertEvent,
} from '@/types/api';
import { isKnownExpertEventKind } from '@/types/api';
import { reportIssue } from '@/lib/telemetry';
import { applyEvent, emptyLedger, seedFromRuns, type LedgerState } from './projection';

export type ConnectionPhase = 'connecting' | 'open' | 'reconnecting' | 'closed';

/** A frame kind this build has no mapping for. Drawn loudly, never silently. */
export interface UnknownFrame {
  id: string;
  kind: string;
  at: string | null;
}

export interface StreamNotice {
  id: string;
  level: 'info' | 'warning' | 'error';
  message: string;
  code: string | null;
  at: string | null;
}

/** Live promotion progress, assembled from `promotion` frames. */
export interface LivePromotion {
  promotionId: number;
  target: PromotionTarget;
  status: PromotionStatus;
  repos: Record<number, PromotionRepo>;
  failure: PromotionFailure | null;
}

interface StreamState {
  chatId: number | null;
  connection: ConnectionPhase;
  attempt: number;
  /** True once a frame has been delivered on this connection. */
  everOpened: boolean;

  ledger: LedgerState;
  /** Turns delivered by the stream, keyed by seq so a replay cannot double them. */
  liveMessages: Record<number, ChatMessage>;
  /** In-flight assistant text per message seq, until the `message` frame lands. */
  deltas: Record<number, string>;
  /** The reasoning trace, accumulated per run. Internal visibility, always. */
  thinking: Record<number, string>;

  status: ChatStatus | null;
  clientVisibleStatus: ClientChatStatus | null;
  composer: ComposerState | null;
  projectFrozen: boolean | null;
  pendingAsk: { ask: AskBlock; answerableByExpert: boolean } | null;
  promotion: LivePromotion | null;
  notices: StreamNotice[];
  contextTokens: number | null;
  contextExhausted: boolean | null;

  unknown: UnknownFrame[];
  /** Bumped whenever the server tells us something off-stream has moved. */
  refetchToken: number;

  attach: (chatId: number) => void;
  seed: (input: {
    runs: readonly RunSummary[];
    status: ChatStatus | null;
    clientVisibleStatus: ClientChatStatus | null;
    composer: ComposerState | null;
    pendingAsk: AskBlock | null;
    promotion: Promotion | null;
  }) => void;
  ingest: (event: SseExpertEvent, cursor: string | null) => void;
  setConnection: (phase: ConnectionPhase, attempt?: number) => void;
  /** The server could not replay from our cursor. Everything local is suspect. */
  hardReset: () => void;
  dismissNotice: (id: string) => void;
}

const blank = {
  connection: 'connecting' as ConnectionPhase,
  attempt: 0,
  everOpened: false,
  ledger: emptyLedger(),
  liveMessages: {} as Record<number, ChatMessage>,
  deltas: {} as Record<number, string>,
  thinking: {} as Record<number, string>,
  status: null as ChatStatus | null,
  clientVisibleStatus: null as ClientChatStatus | null,
  composer: null as ComposerState | null,
  projectFrozen: null as boolean | null,
  pendingAsk: null as StreamState['pendingAsk'],
  promotion: null as LivePromotion | null,
  notices: [] as StreamNotice[],
  contextTokens: null as number | null,
  contextExhausted: null as boolean | null,
  unknown: [] as UnknownFrame[],
};

/** Notices and unknown frames are capped: a stuck run would otherwise grow an
    unbounded list that presents as a slow tab rather than as a bug. */
const NOTICE_CAP = 40;
const UNKNOWN_CAP = 25;

function toLivePromotion(promotion: Promotion): LivePromotion | null {
  if (!promotion) return null;
  const repos: Record<number, PromotionRepo> = {};
  for (const repo of promotion.repos) repos[repo.repo_id] = repo;
  return {
    promotionId: promotion.id,
    target: promotion.target,
    status: promotion.status,
    repos,
    failure: promotion.failure ?? null,
  };
}

export const useStreamStore = create<StreamState>((set, get) => ({
  chatId: null,
  ...blank,
  refetchToken: 0,

  attach: (chatId) => {
    if (get().chatId === chatId) return;
    set({ chatId, ...blank, refetchToken: get().refetchToken });
  },

  setConnection: (phase, attempt = 0) =>
    set((s) => ({ connection: phase, attempt, everOpened: s.everOpened || phase === 'open' })),

  hardReset: () =>
    set((s) => ({
      ...blank,
      chatId: s.chatId,
      connection: s.connection,
      everOpened: s.everOpened,
      refetchToken: s.refetchToken + 1,
    })),

  dismissNotice: (id) => set((s) => ({ notices: s.notices.filter((n) => n.id !== id) })),

  seed: ({ runs, status, clientVisibleStatus, composer, pendingAsk, promotion }) =>
    set((s) => ({
      ledger: seedFromRuns(s.ledger, runs),
      // The stream is fresher than a fetch that may have started before it, so a
      // value the stream already delivered is never overwritten by the seed.
      status: s.status ?? status,
      clientVisibleStatus: s.clientVisibleStatus ?? clientVisibleStatus,
      composer: s.composer ?? composer,
      pendingAsk:
        s.pendingAsk ??
        (pendingAsk && !pendingAsk.answered ? { ask: pendingAsk, answerableByExpert: true } : null),
      promotion: s.promotion ?? (promotion ? toLivePromotion(promotion) : null),
    })),

  ingest: (event, cursor) => {
    const kind = (event as { kind?: string }).kind;

    // Contract drift. The expert stream is not filtered, so an unrecognised kind
    // is a build that is behind the contract rather than a scrub bug — but it is
    // still refused rather than drawn, because this renderer's whole job is to
    // say which turns the client can read.
    if (!kind || !isKnownExpertEventKind(kind)) {
      const token = String(kind ?? 'missing').slice(0, 48);
      reportIssue({ kind: 'unknown_stream_event', token, context: { cursor } });
      set((s) => ({
        unknown: s.unknown
          .concat({
            id: cursor ?? `${Date.now()}-${s.unknown.length}`,
            kind: token,
            at: (event as { at?: string }).at ?? null,
          })
          .slice(-UNKNOWN_CAP),
      }));
      return;
    }

    const patch: Partial<StreamState> = { ledger: applyEvent(get().ledger, event) };

    switch (event.kind) {
      case 'stream_open': {
        // The open frame carries the projected status for our role, which on this
        // stream is the true `chats.status`.
        if (event.status) patch.status = event.status as ChatStatus;
        break;
      }

      case 'status': {
        patch.status = event.status as ChatStatus;
        if (event.client_visible_status) patch.clientVisibleStatus = event.client_visible_status;
        if (event.composer) patch.composer = event.composer;
        if (event.project_frozen !== undefined && event.project_frozen !== null) {
          patch.projectFrozen = event.project_frozen;
        }
        // A status change moves the promotion gate and the diff, neither of which
        // is on this stream.
        patch.refetchToken = get().refetchToken + 1;
        break;
      }

      case 'text_delta': {
        const previous = get().deltas[event.message_seq] ?? '';
        patch.deltas = { ...get().deltas, [event.message_seq]: previous + event.text };
        break;
      }

      case 'thinking': {
        const runId = event.run_id ?? null;
        if (runId !== null) {
          const previous = get().thinking[runId] ?? '';
          patch.thinking = { ...get().thinking, [runId]: previous + event.text };
        }
        break;
      }

      case 'message': {
        const message = event.message;
        if (message) {
          patch.liveMessages = { ...get().liveMessages, [message.seq]: message };
          // The canonical turn replaces whatever the deltas built.
          const deltas = { ...get().deltas };
          delete deltas[message.seq];
          patch.deltas = deltas;
        }
        break;
      }

      case 'ask': {
        const ask = event.ask;
        if (ask && !ask.answered) {
          patch.pendingAsk = { ask, answerableByExpert: (event.answerable_by ?? 'expert') === 'expert' };
        }
        break;
      }

      case 'ask_resolved': {
        const current = get().pendingAsk;
        if (current && current.ask.ask_id === event.ask_id) patch.pendingAsk = null;
        break;
      }

      case 'usage': {
        if (event.chat_context_tokens != null) patch.contextTokens = event.chat_context_tokens;
        if (event.context_exhausted !== undefined) patch.contextExhausted = event.context_exhausted ?? null;
        break;
      }

      case 'notice': {
        patch.notices = get()
          .notices.concat({
            id: cursor ?? `${Date.now()}-${get().notices.length}`,
            level: event.level,
            message: event.message,
            code: event.code ?? null,
            at: event.at ?? null,
          })
          .slice(-NOTICE_CAP);
        break;
      }

      case 'promotion': {
        const previous = get().promotion;
        const repos =
          previous && previous.promotionId === event.promotion_id ? { ...previous.repos } : {};
        if (event.repo) repos[event.repo.repo_id] = event.repo;
        patch.promotion = {
          promotionId: event.promotion_id,
          target: event.target,
          status: event.status,
          repos,
          failure: event.failure ?? (previous?.promotionId === event.promotion_id ? previous.failure : null) ?? null,
        };
        // A promotion that ended changed the chat status, the deploys and the
        // diff against both targets. None of that is on this stream.
        if (event.status === 'succeeded' || event.status === 'failed') {
          patch.refetchToken = get().refetchToken + 1;
        }
        break;
      }

      case 'run_started': {
        // A run in flight blocks a promotion and greys the composer, and neither
        // fact arrives as a frame.
        patch.refetchToken = get().refetchToken + 1;
        break;
      }

      case 'run_finished':
      case 'run_failed': {
        patch.pendingAsk = null;
        patch.refetchToken = get().refetchToken + 1;
        break;
      }

      default:
        break;
    }

    set(patch as StreamState);
  },
}));

/** Merge the fetched history with anything the stream delivered since. */
export function mergeMessages(
  fetched: readonly ChatMessage[],
  live: Record<number, ChatMessage>,
): ChatMessage[] {
  const bySeq = new Map<number, ChatMessage>();
  for (const message of fetched) bySeq.set(message.seq, message);
  // The stream is fresher than a fetch that may have started before it.
  for (const message of Object.values(live)) bySeq.set(message.seq, message);
  return Array.from(bySeq.values()).sort((a, b) => a.seq - b.seq);
}
